import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  limitShift,
  arrow,
  safePolygon,
  useDelayGroupContext,
} from "@floating-ui/react";
import type { Placement } from "@floating-ui/react";
import type { ReferenceType } from "@floating-ui/react-dom"; // eslint-disable-line @typescript-eslint/no-unused-vars
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTooltipLegacyGroup } from "../../providers/HammerProvider/HammerProvider";

export type TooltipLegacyProps = {
  children?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  placement?: Placement;
  offset?: number;
  onOpenChange?: (open: boolean) => void;
  /**
   * Disables the shift of floating element
   * @internal
   */
  disableShift?: boolean;
  /**
   * Disables the flip of floating element
   * @internal
   */
  disableFlip?: boolean;
  fallbackPlacements?: Placement[];
  root?: HTMLElement | React.MutableRefObject<HTMLElement | null> | null;
};

export function useTooltipLegacy({
  defaultOpen = false,
  offset: offsetProp = 5,
  open: controlledOpen,
  placement = "top",
  fallbackPlacements,
  disableFlip,
  disableShift,
  root,
}: TooltipLegacyProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setUncontrolledOpen;

  const { disableDelayGroup } = useTooltipLegacyGroup();

  useEffect(() => {
    if (!defaultOpen && !controlledOpen) {
      disableDelayGroup(true);
    }

    disableDelayGroup(false);
  }, [defaultOpen, controlledOpen, disableDelayGroup]);

  const arrowRef = useRef(null);
  const { delay } = useDelayGroupContext();

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(offsetProp),
      flip({
        crossAxis: !disableFlip ? placement.includes("-") : false,
        mainAxis: !disableFlip,
        fallbackAxisSideDirection: "start",
        fallbackPlacements,
        padding: 5,
      }),
      shift({
        padding: 5,
        mainAxis: !disableShift,
        crossAxis: false,
        limiter: limitShift({
          mainAxis: !disableShift,
        }),
      }),
      arrow({ element: arrowRef, padding: 5 }),
    ],
  });
  const context = data.context;

  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
    delay: delay,
  });
  const focus = useFocus(context, {});
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      root,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data, root],
  );
}
